import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  AssetSchema,
  AssetCreateInputSchema,
  AssetUpdateInputSchema,
  type Asset,
  type AssetCreateInput,
  type AssetUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `asset` (bien suivi, org-wide).
 *
 * PATRON de référence des familles du socle (cf. asset dans @taki/data-model).
 * Différence clé avec l'ancien repository `contacts` : l'entrée est VALIDÉE par
 * Zod (`AssetCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit
 * jamais un document pour découvrir ensuite qu'il est invalide.
 */

export type { AssetCreateInput };

/** Nom de la collection Firestore de la famille asset. */
export const ASSETS_COLLECTION = 'assets';

/**
 * Repository typé pour la famille `asset`.
 * Toute lecture est validée par `AssetSchema`, toute écriture par
 * `AssetCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface AssetsRepository {
  create(input: AssetCreateInput): Promise<Asset>;
  list(options?: ListOptions): Promise<Asset[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Asset | null>;
  update(id: string, patch: AssetUpdateInput): Promise<Asset>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Asset>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Asset>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Asset>;
}

const assetConverter = createConverter<Asset>((raw: DocumentData) => AssetSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createAssetsRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): AssetsRepository {
  const col = collection(db, ASSETS_COLLECTION).withConverter(assetConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Asset>(db, col, ASSETS_COLLECTION, getActorId);

  return {
    async create(input: AssetCreateInput): Promise<Asset> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Correctif du modèle historique qui validait après setDoc.)
      const fields = AssetCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.label !== undefined) optional['label'] = fields.label;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.reference !== undefined) optional['reference'] = fields.reference;
      if (fields.ownerContactId !== undefined) optional['ownerContactId'] = fields.ownerContactId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        name: fields.name,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Asset);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return AssetSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Asset[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Asset | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: AssetUpdateInput): Promise<Asset> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = AssetUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, ASSETS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`asset ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
